import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, User, Calendar, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { TERMS_VERSION } from "../legal/legalContent";
import { errText, maxDob } from "./CompleteRegistration";
import api from "../lib/api";
import { BrandLogo } from "../components/BrandLogo";
import { trackEvent, sendAttribution } from "../lib/track";

const inputCls = "w-full rounded-xl bg-[#0C0A0A]/60 border border-[#D4AF37]/20 focus:border-[#D4AF37]/60 outline-none pl-11 pr-4 py-3 text-[#FBF8F3] placeholder:text-[#9E978E]";

export default function Auth() {
  const { user, mfaPending, login, register, verifyOtp, resendOtp } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ email: "", password: "", display_name: "", date_of_birth: "" });
  const [busy, setBusy] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [otp, setOtp] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [resendLeft, setResendLeft] = useState(0);
  const resetToken = params.get("reset_token") || "";
  const [resetPw, setResetPw] = useState({ password: "", confirm: "" });
  const [resetDone, setResetDone] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => { if (user) navigate("/discover"); }, [user]);

  useEffect(() => {
    if (mode !== "register") return;
    try {
      if (!sessionStorage.getItem("db_reg_start")) {
        sessionStorage.setItem("db_reg_start", "1");
        trackEvent("register_start", "/auth");
      }
    } catch (e) { trackEvent("register_start", "/auth"); }
  }, [mode]);

  useEffect(() => {
    if (!mfaPending) { setSecondsLeft(0); setResendLeft(0); return; }
    const tick = () => {
      if (mfaPending.expiresAt) setSecondsLeft(Math.max(0, Math.round((new Date(mfaPending.expiresAt).getTime() - Date.now()) / 1000)));
      if (mfaPending.cooldownUntil) setResendLeft(Math.max(0, Math.round((mfaPending.cooldownUntil - Date.now()) / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [mfaPending?.expiresAt, mfaPending?.cooldownUntil, mfaPending]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") {
        if (!ageConfirmed) { setBusy(false); return toast.error("Please confirm you are 18 or older."); }
        if (!termsAgreed) { setBusy(false); return toast.error("Please agree to the Terms of Service and Privacy Policy."); }
        await register({ ...form, accepted_terms_version: TERMS_VERSION });
        sendAttribution();
        toast.success("Welcome to DateBid");
        navigate("/onboarding");
      } else {
        const res = await login(form.email, form.password);
        if (res && res.otp_required) { toast.success("We emailed you a verification code"); }
        else { sendAttribution(); toast.success("Welcome back"); navigate("/discover"); }
      }
    } catch (err) {
      toast.error(errText(err, "Authentication failed"));
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await verifyOtp(otp.trim());
      toast.success("Verified");
      navigate(u?.role === "admin" ? "/admin" : "/discover");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    try { await resendOtp(); setOtp(""); toast.success("New code sent — please use the most recent code"); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not resend"); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: form.email });
      setForgotSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send reset email");
    } finally { setBusy(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (resetPw.password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (resetPw.password !== resetPw.confirm) return toast.error("Passwords do not match.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token: resetToken, password: resetPw.password });
      setResetDone(true);
      toast.success("Password reset — please sign in");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password");
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/discover";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (mfaPending) {
    const hasTimer = !!mfaPending.expiresAt;
    const expired = hasTimer && secondsLeft === 0;
    const mmss = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, "0")}`;
    return (
      <div className="db-grain min-h-screen flex items-center justify-center p-4 relative" style={{ background: "radial-gradient(900px 500px at 50% -10%, rgba(117,26,50,0.25), transparent 60%), #0C0A0A" }}>
        <div className="relative z-10 w-full max-w-md db-fade-up">
          <div className="text-center mb-7">
            <span className="inline-block"><BrandLogo variant="wordmark" className="h-14 w-auto mx-auto" /></span>
            <p className="text-sm text-[#9E978E] mt-2">Administrator verification — enter the 6-digit code we emailed to {mfaPending.user?.email}.</p>
          </div>
          <div className="db-glass rounded-3xl border border-[#D4AF37]/20 p-7">
            {hasTimer && (
              expired ? (
                <div data-testid="admin-otp-expired" className="rounded-xl bg-[#8B263E]/20 border border-[#8B263E]/40 px-4 py-2.5 mb-4 text-sm text-[#E58CA0] text-center">
                  Your verification code has expired. Please request a new code.
                </div>
              ) : (
                <div data-testid="admin-otp-countdown" className="rounded-xl bg-[#541224]/25 border border-[#D4AF37]/25 px-4 py-2.5 mb-4 text-sm text-[#D8D2C9] text-center">
                  Verification code expires in <span className="text-[#D4AF37] font-semibold">{mmss}</span>
                </div>
              )
            )}
            <form onSubmit={verify} className="space-y-4">
              <input data-testid="admin-otp-input" required value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))} placeholder="Verification code" inputMode="numeric" className={inputCls + " tracking-[0.4em] text-center text-lg"} />
              <button data-testid="admin-otp-verify" type="submit" disabled={busy || expired} className="db-offer-btn w-full rounded-full py-3.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                {busy ? "Verifying…" : "Verify & Continue"} <ArrowRight size={16} />
              </button>
            </form>
            <button data-testid="admin-otp-resend" onClick={resend} disabled={resendLeft > 0}
              className="w-full mt-4 text-xs text-[#C9A96E] hover:text-[#D4AF37] disabled:text-[#9E978E] disabled:cursor-not-allowed">
              {resendLeft > 0
                ? `Resend available in ${Math.floor(resendLeft / 60)}:${String(resendLeft % 60).padStart(2, "0")}`
                : (expired ? "Request a new code" : "Didn't get it? Resend code")}
            </button>
          </div>
          <p className="text-center text-xs text-[#9E978E] mt-5">Codes expire in 15 minutes and are single-use. Always use the most recent code. Never share it with anyone.</p>
        </div>
      </div>
    );
  }

  if (resetToken) {
    return (
      <div className="db-grain min-h-screen flex items-center justify-center p-4 relative" style={{ background: "radial-gradient(900px 500px at 50% -10%, rgba(117,26,50,0.25), transparent 60%), #0C0A0A" }}>
        <div className="relative z-10 w-full max-w-md db-fade-up">
          <div className="text-center mb-7">
            <Link to="/" className="inline-block"><BrandLogo variant="wordmark" className="h-14 w-auto mx-auto" /></Link>
            <p className="text-sm text-[#9E978E] mt-2">Choose a new password</p>
          </div>
          <div className="db-glass rounded-3xl border border-[#D4AF37]/20 p-7">
            {resetDone ? (
              <div data-testid="reset-success" className="text-center space-y-4">
                <p className="text-[#D8D2C9] text-sm">Your password has been reset successfully.</p>
                <button data-testid="reset-goto-login" onClick={() => navigate("/auth")} className="db-offer-btn w-full rounded-full py-3.5 text-sm font-semibold">Go to Sign In</button>
              </div>
            ) : (
              <form onSubmit={submitReset} className="space-y-4">
                <div className="relative">
                  <Lock size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9E978E]" />
                  <input data-testid="reset-password-input" required type="password" value={resetPw.password} onChange={(e) => setResetPw({ ...resetPw, password: e.target.value })} placeholder="New password (min 8 characters)" className={inputCls} />
                </div>
                <div className="relative">
                  <Lock size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9E978E]" />
                  <input data-testid="reset-confirm-input" required type="password" value={resetPw.confirm} onChange={(e) => setResetPw({ ...resetPw, confirm: e.target.value })} placeholder="Confirm new password" className={inputCls} />
                </div>
                <button data-testid="reset-submit-btn" type="submit" disabled={busy} className="db-offer-btn w-full rounded-full py-3.5 text-sm font-semibold flex items-center justify-center gap-2">
                  {busy ? "Please wait…" : "Reset Password"} <ArrowRight size={16} />
                </button>
                <button type="button" data-testid="reset-back-link" onClick={() => navigate("/auth")} className="w-full text-center text-xs text-[#9E978E] hover:text-[#D4AF37] transition-colors">Back to sign in</button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (mode === "forgot") {
    return (
      <div className="db-grain min-h-screen flex items-center justify-center p-4 relative" style={{ background: "radial-gradient(900px 500px at 50% -10%, rgba(117,26,50,0.25), transparent 60%), #0C0A0A" }}>
        <div className="relative z-10 w-full max-w-md db-fade-up">
          <div className="text-center mb-7">
            <Link to="/" className="inline-block"><BrandLogo variant="wordmark" className="h-14 w-auto mx-auto" /></Link>
            <p className="text-sm text-[#9E978E] mt-2">Reset your password</p>
          </div>
          <div className="db-glass rounded-3xl border border-[#D4AF37]/20 p-7">
            {forgotSent ? (
              <div data-testid="forgot-sent" className="text-center space-y-4">
                <p className="text-[#D8D2C9] text-sm">If an account with that email exists, we've sent password reset instructions. Please check your inbox (and spam folder).</p>
                <button data-testid="forgot-back-to-login" onClick={() => { setMode("login"); setForgotSent(false); }} className="db-offer-btn w-full rounded-full py-3.5 text-sm font-semibold">Back to Sign In</button>
              </div>
            ) : (
              <form onSubmit={submitForgot} className="space-y-4">
                <p className="text-sm text-[#9E978E]">Enter your account email and we'll send you a secure link to reset your password.</p>
                <div className="relative">
                  <Mail size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9E978E]" />
                  <input data-testid="forgot-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="Email address" className={inputCls} />
                </div>
                <button data-testid="forgot-submit-btn" type="submit" disabled={busy} className="db-offer-btn w-full rounded-full py-3.5 text-sm font-semibold flex items-center justify-center gap-2">
                  {busy ? "Please wait…" : "Send Reset Link"} <ArrowRight size={16} />
                </button>
                <button type="button" data-testid="forgot-back-link" onClick={() => setMode("login")} className="w-full text-center text-xs text-[#9E978E] hover:text-[#D4AF37] transition-colors">Back to sign in</button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="db-grain min-h-screen flex items-center justify-center p-4 relative" style={{ background: "radial-gradient(900px 500px at 50% -10%, rgba(117,26,50,0.25), transparent 60%), #0C0A0A" }}>
      <div className="relative z-10 w-full max-w-md db-fade-up">
        <div className="text-center mb-7">
          <Link to="/" className="inline-block"><BrandLogo variant="wordmark" className="h-14 w-auto mx-auto" /></Link>
          <p className="text-sm text-[#9E978E] mt-2">{mode === "register" ? "Create your account — it takes a minute." : "Welcome back to the marketplace."}</p>
        </div>

        <div className="db-glass rounded-3xl border border-[#D4AF37]/20 p-7">
          <div className="flex rounded-full bg-[#0C0A0A]/60 p-1 mb-6">
            <button data-testid="auth-tab-login" onClick={() => setMode("login")} className={`flex-1 rounded-full py-2 text-sm font-medium transition-colors ${mode === "login" ? "bg-[#541224] text-[#FBF8F3]" : "text-[#9E978E]"}`}>Sign In</button>
            <button data-testid="auth-tab-register" onClick={() => setMode("register")} className={`flex-1 rounded-full py-2 text-sm font-medium transition-colors ${mode === "register" ? "bg-[#541224] text-[#FBF8F3]" : "text-[#9E978E]"}`}>Join</button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="relative">
                <User size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9E978E]" />
                <input data-testid="auth-name-input" required value={form.display_name} onChange={set("display_name")} placeholder="Display name" className={inputCls} />
              </div>
            )}
            <div className="relative">
              <Mail size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9E978E]" />
              <input data-testid="auth-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="Email address" className={inputCls} />
            </div>
            <div className="relative">
              <Lock size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9E978E]" />
              <input data-testid="auth-password-input" required type="password" value={form.password} onChange={set("password")} placeholder="Password (min 8 characters)" className={inputCls} />
            </div>
            {mode === "register" && (
              <>
                <div className="relative">
                  <Calendar size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9E978E]" />
                  <input data-testid="auth-dob-input" required type="date" max={maxDob()} value={form.date_of_birth} onChange={set("date_of_birth")} className={inputCls} />
                </div>
                <label className="flex items-start gap-3 text-sm text-[#D8D2C9] cursor-pointer">
                  <input data-testid="auth-age-checkbox" type="checkbox" checked={ageConfirmed} onChange={(e) => setAgeConfirmed(e.target.checked)} className="mt-1 accent-[#D4AF37]" />
                  <span>I confirm I am at least 18 years old and agree to meet responsibly and in public.</span>
                </label>
                {(
                  <label className="flex items-start gap-3 text-sm text-[#D8D2C9] cursor-pointer">
                    <input data-testid="auth-terms-checkbox" type="checkbox" checked={termsAgreed} onChange={(e) => setTermsAgreed(e.target.checked)} className="mt-1 accent-[#D4AF37]" />
                    <span>I agree to the <a href="/legal/terms" target="_blank" rel="noreferrer" className="text-[#D4AF37] underline">Terms of Service</a> and acknowledge the <a href="/legal/privacy" target="_blank" rel="noreferrer" className="text-[#D4AF37] underline">Privacy Policy</a>.</span>
                  </label>
                )}
              </>
            )}

            <button data-testid="auth-submit-btn" type="submit" disabled={busy} className="db-offer-btn w-full rounded-full py-3.5 text-sm font-semibold flex items-center justify-center gap-2">
              {busy ? "Please wait…" : (mode === "register" ? "Create Account" : "Sign In")} <ArrowRight size={16} />
            </button>
            {mode === "login" && (
              <button type="button" data-testid="auth-forgot-link" onClick={() => setMode("forgot")} className="w-full text-center text-xs text-[#9E978E] hover:text-[#D4AF37] transition-colors">Forgot your password?</button>
            )}
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-white/10" /><span className="text-xs text-[#9E978E]">or</span><div className="flex-1 h-px bg-white/10" />
          </div>

          <button data-testid="auth-google-btn" onClick={googleLogin} className="w-full rounded-full py-3 text-sm font-medium border border-[#D4AF37]/25 text-[#FBF8F3] hover:border-[#D4AF37]/60 transition-colors flex items-center justify-center gap-2 bg-[#0C0A0A]/40">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C41.5 36.8 44 31 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
            Continue with Google
          </button>
        </div>
        <p className="text-center text-xs text-[#9E978E] mt-5">Members must be 18+. Meet in public and keep personal details private.</p>
      </div>
    </div>
  );
}
